// ── Position Types ─────────────────────────────────────────────────────────────
//
// Domain types and API contracts — what clients send and receive.
// No Mongoose, no DB details. Those live in position.model.ts.

import { z } from "zod";

// ── PositionStatus ─────────────────────────────────────────────────────────────

export const PositionStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DRAFT: "draft",
} as const;

export type PositionStatus = (typeof PositionStatus)[keyof typeof PositionStatus];

// ── Response DTO ───────────────────────────────────────────────────────────────

export const Position = z.object({
  id: z.string().describe("Unique resource identifier"),
  title: z.string().min(1).describe("Position title"),
  job_description: z.string().describe("Full job description text"),
  team_id: z.string().min(1).describe("Owning team ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  ai_skills: z.array(z.string()).optional().describe("Required AI skill names"),
  tools_to_integrate: z
    .array(z.string())
    .optional()
    .describe("Tools to integrate for this position"),
  monthly_budget: z.number().optional().describe("Monthly budget allocation"),
  status: z
    .enum(Object.values(PositionStatus) as [string, ...string[]])
    .optional()
    .describe("Position lifecycle status"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
});

export type Position = z.infer<typeof Position>;

// ── Create DTO ─────────────────────────────────────────────────────────────────

export const PositionCreate = Position.omit({
  id: true,
  created_at: true,
  updated_at: true,
}).extend({
  ai_skills: z.array(z.string()).default([]),
  tools_to_integrate: z.array(z.string()).default([]),
  monthly_budget: z.number().default(0),
});

export type PositionCreate = z.infer<typeof PositionCreate>;

// ── Update DTO ─────────────────────────────────────────────────────────────────
// `ai_skills`, `tools_to_integrate`, and `monthly_budget` are re-declared without
// .default() so Zod does not inject defaults when these fields are absent from
// a PATCH body — preserving true partial-update semantics.

export const PositionUpdate = PositionCreate.omit({
  team_id: true,
  created_by: true,
})
  .partial()
  .extend({
    ai_skills: z.array(z.string()).optional(),
    tools_to_integrate: z.array(z.string()).optional(),
    monthly_budget: z.number().optional(),
  });

export type PositionUpdate = z.infer<typeof PositionUpdate>;
